import {
  Link,
  Outlet,
  json,
  useLoaderData,
  useLocation,
} from '@remix-run/react'
import type { LoaderFunctionArgs } from '@remix-run/server-runtime'
import RedIcon from '~/components/RedIcon'
import feedbackimg from '~/images/logos/feedback-image.jpeg'
import { fetchReportById } from '~/models/report.v1.server'
import { commitSession, getSession } from '~/models/sessions.server'

export async function loader({ params, request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get('Cookie'))
  const [reportResult] = await Promise.allSettled([fetchReportById(params.id!)])

  if (reportResult.status === 'rejected') {
    throw new Response('Error fetching report', { status: 500 })
  }

  const isPublicPromo = reportResult.value.isPublicPromo

  return json(
    {
      hasProvidedFeedback: session.get('feedbackGiven'),
      isPublicPromo,
    },
    { headers: { 'Set-Cookie': await commitSession(session) } },
  )
}

export default function () {
  const { hasProvidedFeedback, isPublicPromo } = useLoaderData<typeof loader>()

  return (
    <div className="prose prose-h4:font-semibold prose-h4:text-sm/5 mb-6 max-w-full">
      <h3 className="md:text-h3 text-sm mb-3">Travel Tips</h3>
      <p>Get expert tips for your trip.</p>
      {hasProvidedFeedback || isPublicPromo ? (
        <div className="my-4 flex flex-col gap-4">
          <div className="rounded-xl p-4 border-neutral-300 border">
            <div className="flex flex-col gap-2">
              <RedIcon iconsvg="calendar" />
              <p className="font-semibold">
                When Departing from Sydney, Check your gate before heading to
                the lounge!
              </p>
              <p>
                Make sure you know how far your gate is from the lounge - gates
                50-63 are a 15-minute walk away from gates 8-37!
              </p>
            </div>
          </div>
          <div className="rounded-xl p-4 border-neutral-300 border">
            <div className="flex flex-col gap-2">
              <RedIcon iconsvg="ticket" />
              <p className="font-semibold">
                When Departing from Melbourne, Book your Skybus online in
                advance!
              </p>
              <p>
                Parking can get expensive, so the Skybus is often the cheapest
                way to get from the CBD to the Airport. If you book online, your
                ticket is flexible for the next 3 months, and also saves you
                cash.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="my-8 flex flex-col-reverse md:flex-row rounded-md min-h-48 w-full">
          <div className="bg-[#FCDFB4] p-4 rounded-b-md md:rounded-l-md md:rounded-br-none md:w-3/5 w-full">
            <h3 className="text-h3 mt-0 mb-2">Enrich your travel experience</h3>
            <p className="mt-0">
              Let us know how we did and unlock expert travel tips.
            </p>
            <Link to="form" preventScrollReset>
              <button
                type="button"
                className="bg-white text-black rounded-md py-2 px-5 mt-3 font-semibold"
              >
                Give feedback
              </button>
            </Link>
          </div>
          <div className="md:w-2/5 w-full">
            <img
              src={feedbackimg}
              alt="Concierge feedback"
              className="w-full h-full object-cover rounded-t-md md:rounded-r-md md:rounded-tl-none my-0"
            />
          </div>
        </div>
      )}
      <Outlet />
    </div>
  )
}
